/**
 * Documents attached to one Business Discovery, in the Sales Console.
 *
 * INTERNAL, and read-only: see what the client attached and open one. Clients
 * attach documents themselves, from their own Business Discovery page
 * (`src/client/DocumentUpload.jsx`); the console does not upload on their
 * behalf. The files are kept in MinIO and served by the backend.
 */

import { useCallback, useEffect, useId, useState } from 'react';
import { documentDownloadUrl, fetchDocuments } from '../api/client';
import '../styles/Console.css';

const STATUS = {
  uploaded: { label: 'Stored', tone: 'neutral' },
  processing: { label: 'Reading…', tone: 'info' },
  processed: { label: 'Text extracted', tone: 'ok' },
  failed: { label: 'Text unavailable', tone: 'warn' },
};

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileType(doc) {
  const extension = doc.original_filename?.split('.').pop();
  if (extension && extension !== doc.original_filename) return extension.toUpperCase();
  return doc.content_type || 'File';
}

/** What a document row says about its extracted text, in plain words. */
function textNote(doc) {
  if (doc.extracted_text_available) {
    return `Text read locally${doc.text_truncated ? ' (truncated)' : ''}`;
  }
  return doc.extraction_note || doc.extraction_error || 'No text extracted';
}

function formatUploaded(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function DocumentsPanel({ sessionId }) {
  const [documents, setDocuments] = useState([]);
  const headingId = useId();

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      const body = await fetchDocuments(sessionId);
      setDocuments(body.documents || []);
    } catch {
      // A listing that cannot be read is not worth an alarm: the panel simply
      // shows nothing until the next successful load.
      setDocuments([]);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  if (!sessionId) return null;

  return (
    <section className="nsc-card" aria-labelledby={headingId}>
      <div className="nsc-card__header">
        <div className="nsc-card__heading">
          <h2 id={headingId} className="nsc-card__title">Documents</h2>
          <p className="nsc-card__description">
            Process, requirements or sample files the client attached to this
            Business Discovery.
          </p>
        </div>
      </div>

      <div className="nsc-card__body">
        {documents.length === 0 ? (
          <p className="nsc-empty-value">No documents attached.</p>
        ) : (
          <ul className="nsc-file-list">
            {documents.map((doc) => {
              const status = STATUS[doc.processing_status] || { label: doc.processing_status, tone: 'neutral' };
              return (
                <li key={doc.document_id} className="nsc-file">
                  <div className="nsc-file__main">
                    <span className="nsc-file__name">{doc.original_filename}</span>
                    <span className="nsc-file__meta">
                      {fileType(doc)} · {formatSize(doc.size_bytes)} · {textNote(doc)}
                    </span>
                    {formatUploaded(doc.uploaded_at) && (
                      <span className="nsc-file__meta">Added {formatUploaded(doc.uploaded_at)}</span>
                    )}
                  </div>
                  <div className="nsc-file__side">
                    <span className={`nsc-badge nsc-badge--${status.tone}`}>{status.label}</span>
                    <a
                      className="nsc-button nsc-button--ghost"
                      href={documentDownloadUrl(sessionId, doc.document_id)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View
                    </a>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
